import React from 'react';
import {
  Alert,
  AlertTitle,
  Typography,
  Box,
  SxProps,
  Theme,
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';

export interface AlertBannerProps {
  severity?: 'error' | 'warning' | 'info' | 'success';
  title?: string;
  message?: string;
  description?: string;
  icon?: React.ReactElement<SvgIconComponent>;
  sx?: SxProps<Theme>;
  showTitle?: boolean;
  showIcon?: boolean;
  customIcon?: React.ReactElement;
}

const AlertBanner: React.FC<AlertBannerProps> = ({
  severity = 'warning',
  title,
  message,
  description,
  icon,
  sx = {},
  showTitle = true,
  showIcon = true,
  customIcon,
}) => {
  const defaultSx: SxProps<Theme> = {
    my: 2,
    borderRadius: '8px',
    '& .MuiAlert-message': {
      fontSize: '0.9rem',
    },
    '& .MuiAlert-icon': {
      alignItems: 'flex-start',
    },
  };

  const severityStyles: Record<string, SxProps<Theme>> = {
    error: {
      border: '1px solid #d32f2f',
    },
    warning: {
      border: '1px solid rgba(255, 152, 0, 0.3)',
    },
    info: {
      border: '1px solid rgba(33, 150, 243, 0.3)',
    },
    success: {
      border: '1px solid #2e7d32',
    },
  };

  const mergedSx = {
    ...defaultSx,
    ...severityStyles[severity],
    ...sx,
  };

  return (
    <Alert
      severity={severity}
      icon={showIcon ? customIcon || icon : false}
      sx={mergedSx}
    >
      {showTitle && title && (
        <AlertTitle
          sx={{
            fontWeight: 700,
            fontSize: '0.95rem',
            color: `${severity}.main`,
            marginBottom: '8px',
          }}
        >
          {title}
        </AlertTitle>
      )}

      <Box>
        {message && (
          <Typography
            variant="body1"
            sx={{
              color: 'text.primary',
              mb: description ? 1.5 : 0,
              fontSize: '0.8rem',
              fontWeight: 300,
            }}
          >
            {message}
          </Typography>
        )}

        {description && (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontSize: '0.75rem',
              color: 'text.secondary',
              fontStyle: 'italic',
              opacity: 0.8,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Alert>
  );
};

export default AlertBanner;
